import { cioFacetToUrlParam } from "~/hec/config/facet-mapping";
import type {
  ICategoryEnrichmentEntry,
  LayoutType,
  NormalizedCategory,
  SfccCategoryEntry,
} from "~/hec/types/plp";

/**
 * Merges SFCC subcategories with Builder enrichment entries.
 * SFCC is the base — all subcategories are shown. Builder entries act as
 * overrides, adding icons, custom names, ranks, or excluding specific entries.
 */
export function mergeWithBuilderEnrichment(
  sfccCategories: SfccCategoryEntry[],
  enrichmentData: ICategoryEnrichmentEntry[],
): NormalizedCategory[] {
  const builderByShortName = Array.isArray(enrichmentData)
    ? enrichmentData.reduce(
        (acc: Record<string, ICategoryEnrichmentEntry>, entry) => {
          const shortName = entry?.data?.categoryShortName;
          if (typeof shortName === "string") acc[shortName] = entry;
          return acc;
        },
        {},
      )
    : {};

  return sfccCategories
    .map(cat => {
      const builderEntry = builderByShortName[cat.id];
      return {
        id: cat.id,
        name: builderEntry?.name ?? cat.name,
        categoryShortName: cat.id,
        path: cat.path,
        categoryIcon: builderEntry?.data?.categoryIcon,
        exclude: builderEntry?.data?.excludeFromCategoryIconsBanner === true,
        iconRank: builderEntry?.data?.iconRank,
      };
    })
    .filter(cat => !cat.exclude)
    .sort((a, b) => {
      const aHasRank = typeof a.iconRank === "number";
      const bHasRank = typeof b.iconRank === "number";
      if (aHasRank && bHasRank) return (a.iconRank ?? 0) - (b.iconRank ?? 0);
      if (aHasRank) return -1;
      if (bHasRank) return 1;
      return 0;
    });
}

/**
 * Applies a filter object's price/search fields to the given URLSearchParams
 * and returns a new URLSearchParams instance.
 */
export function applyFilter(
  searchParams: URLSearchParams,
  filter: Record<string, string>,
): URLSearchParams {
  if (!filter || typeof filter !== "object") return searchParams;
  const params = new URLSearchParams(searchParams.toString());
  if (filter["maximumPrice"]) params.set("pmax", filter["maximumPrice"]);
  if (filter["minimumPrice"]) params.set("pmin", filter["minimumPrice"]);
  if (filter["searchParameters"]) {
    new URLSearchParams(filter["searchParameters"]).forEach((value, key) => {
      // Normalize a curated pill's facet param to the emitted form so curated
      // links match prod too (`productType` → `product-type`, SFCC-11638).
      // cioFacetToUrlParam is idempotent on the already-legacy form, so an
      // inbound `product-type` and every non-facet key pass through unchanged.
      params.set(cioFacetToUrlParam(key), value);
    });
  }
  // Reset pagination — applying a filter must return to page 1 so the full
  // filtered result set is shown, not a stale page-N slice left over from
  // Load More (SFCC-11307).
  params.delete("page");
  return params;
}

export function determineLayoutClasses(layoutType?: LayoutType): string {
  switch (layoutType) {
    case "scroll":
      return "flex flex-row gap-[10px] justify-start items-start overflow-auto max-w-[350px] sm:container md:flex-nowrap sm:items-center sm:justify-center";
    case "wrap":
      return "flex flex-wrap gap-[10px] justify-center items-center w-full max-w-screen";
    case "grid":
      return "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[10px] justify-center items-center w-full max-w-screen";
    default:
      return "flex flex-row gap-[10px] justify-center items-center w-full max-w-screen";
  }
}
